import { ChangeEvent, FormEvent, memo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { terminateCard } from "../../../services/userServices";
import { CardDetail, CardStatus } from "../../../types/CardDetail";
import style from "./index.module.scss";
import { QueryKeyEnum } from "../../../types";

const VALID_CONFIRMATION_MESSAGE = "TERMINATE";

interface CardTerminationFormProps {
  userCardData: CardDetail;
  onCancel: () => void;
}

function CardTerminationForm(props: CardTerminationFormProps) {
  const { userId } = useParams();
  const queryClient = useQueryClient();
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const terminateCardMutation = useMutation({
    mutationFn: () =>
      terminateCard(
        props.userCardData.cardOpaqueId,
        props.userCardData.cardNumberOpaqueId
      ),
    onSuccess: () => {
      queryClient.setQueryData<CardDetail>(
        [QueryKeyEnum.GetCardDetail, userId],
        {
          ...props.userCardData,
          status: CardStatus.PermBlock,
        }
      );
      props.onCancel();
    },
    onError: (error: Error) => {
      toast.error(
        `An error happened. Please reach out to the engineering team with the following data: error: ${error.message}, userId: ${userId}`
      );
    },
  });

  const handleChangeConfirmationMessage = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmationMessage(e.target.value);
  };

  const handleTerminateCard = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (confirmationMessage === VALID_CONFIRMATION_MESSAGE) {
      terminateCardMutation.mutate();
    }
  };

  return (
    <div className={style.formWrapper}>
      <div className={style.formHeading}>
        <h1 className={style.title}>
          Are you sure you want to terminate this card?
        </h1>
        <p className={style.description}>
          This action is irreversible. Enter ‘TERMINATE’ in the field below to
          confirm.
        </p>
      </div>
      <form className={style.formContent} onSubmit={handleTerminateCard}>
        <input
          autoFocus
          type="text"
          data-status={confirmationMessage}
          placeholder={VALID_CONFIRMATION_MESSAGE}
          className={style.input}
          onChange={handleChangeConfirmationMessage}
        />
        <div className={style.buttonsWrapper}>
          <button
            disabled={
              confirmationMessage !== VALID_CONFIRMATION_MESSAGE ||
              terminateCardMutation.isLoading
            }
            type="submit"
            className={style.submitButton}
          >
            Terminate card
          </button>
          <button
            type="button"
            className={style.cancelButton}
            onClick={props.onCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default memo(CardTerminationForm);
